import { useState, useCallback, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';

const CONTACT_FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9oouoiftmtnfcfh0be0';

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      return;
    }
    formData.delete('phone_alt');

    setStatus('loading');
    setErrorMsg('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(CONTACT_FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const serverMsg =
          ((parsed as { meta?: { message?: string } }).meta?.message) ||
          ((parsed as { message?: string }).message) ||
          ((parsed as { meta?: { detail?: string } }).meta?.detail) ||
          responseText ||
          '提交失败，请稍后重试';
        setStatus('error');
        setErrorMsg(serverMsg);
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络连接后重试');
    }
  }, []);

  return (
    <>
      {status === 'success' && (
        <div className="mb-6 p-4 rounded-lg bg-accent-50 border border-accent-200/70 text-accent-800 text-sm">
          <i className="ri-checkbox-circle-line mr-2"></i>
          感谢您的留言！我们会尽快回复您。
        </div>
      )}
      {status === 'error' && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200/70 text-red-800 text-sm">
          <i className="ri-error-warning-line mr-2"></i>
          {errorMsg}
        </div>
      )}
      <form
        ref={formRef}
        data-readdy-form=""
        id="about-contact-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-name" className="block text-foreground-700 text-sm mb-1.5">
              姓名
            </label>
            <input
              type="text"
              id="contact-name"
              name="name"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-foreground-700 text-sm mb-1.5">
              邮箱
            </label>
            <input
              type="email"
              id="contact-email"
              name="email"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              placeholder="请输入您的邮箱"
            />
          </div>
        </div>
        <div>
          <label htmlFor="contact-subject" className="block text-foreground-700 text-sm mb-1.5">
            主题
          </label>
          <input
            type="text"
            id="contact-subject"
            name="subject"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
            placeholder="请输入留言主题"
          />
        </div>
        <div>
          <label htmlFor="contact-message" className="block text-foreground-700 text-sm mb-1.5">
            留言内容
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
            placeholder="请输入您的留言，最多500字"
          ></textarea>
        </div>
        <div className="spam-protection-field">
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-3 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
        >
          {status === 'loading' ? '发送中...' : '发送留言'}
        </button>
      </form>
    </>
  );
}

export default function AboutPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于布丁漫画网',
            description:
              '布丁漫画网是专注于下拉式连续阅读体验的漫画平台，提供韩漫、日漫、国漫等全类型漫画免费在线阅读。',
            url: 'https://budingmanhua.com/about',
            about: {
              '@type': 'Organization',
              name: '布丁漫画网',
              url: 'https://budingmanhua.com',
              description:
                '布丁漫画网 - 专注下拉式连续阅读的漫画平台',
            },
          }),
        }}
      />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6">
              关于布丁漫画网
            </h1>

            <div className="space-y-8">
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-xl text-foreground-900 mb-4" id="brand">品牌介绍</h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  布丁漫画网成立于2020年，是国内领先的<strong>下拉式连续阅读漫画平台</strong>。
                  我们致力于为广大漫画爱好者提供最流畅、最沉浸的漫画阅读体验。
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  布丁漫画网独创的<strong>下拉式连续阅读模式</strong>，彻底改变了传统翻页阅读方式。
                  用户只需向下滚动，漫画内容即会自动连续展示，无需频繁点击翻页按钮，
                  让阅读更加流畅自然，完全沉浸在漫画的世界中。
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  布丁漫画网汇聚了<strong>韩漫、日漫、国漫</strong>等多种类型的优质漫画资源，
                  涵盖热血、玄幻、都市、悬疑、搞笑、科幻、冒险等丰富分类，
                  满足不同漫画爱好者的需求。我们与众多国内外漫画创作者和平台合作，
                  为用户提供正版、高品质的漫画内容。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-book-read-line', title: '10,000+', desc: '漫画资源' },
                  { icon: 'ri-user-heart-line', title: '500万+', desc: '月活跃用户' },
                  { icon: 'ri-thumb-up-line', title: '99.9%', desc: '用户好评率' },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${item.icon} text-accent-600 text-xl`}></i>
                    </div>
                    <p className="font-heading text-2xl text-foreground-900 mb-1">{item.title}</p>
                    <p className="text-foreground-500 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-xl text-foreground-900 mb-4" id="reputation">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      name: '漫画爱好者小李',
                      rating: 5,
                      comment: '布丁漫画的下拉式阅读真的太爽了！以前看漫画老是要翻页很麻烦，现在直接往下滑就行，一口气能看完一整部，强烈推荐！',
                      date: '2026-07-28',
                    },
                    {
                      name: '二次元少女',
                      rating: 5,
                      comment: '布丁漫画网的资源超全，韩漫、日漫、国漫都有。而且界面很清爽，没有乱七八糟的广告，阅读体验一级棒！',
                      date: '2026-07-25',
                    },
                    {
                      name: '夜猫子读者',
                      rating: 5,
                      comment: '每天晚上都用布丁漫画看漫画，下拉式连续阅读模式让我可以躺着不用动就能一直看。更新速度也很快，追更必备！',
                      date: '2026-07-20',
                    },
                    {
                      name: '漫画深度粉',
                      rating: 4,
                      comment: '布丁漫画的下拉式阅读确实很新颖，比传统翻页方便多了。漫画种类也很丰富，如果能增加更多经典老漫画就更完美了。',
                      date: '2026-07-15',
                    },
                  ].map((review) => (
                    <div
                      key={review.name}
                      className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                          <span className="text-accent-700 font-heading text-sm">
                            {review.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-foreground-900 font-medium text-sm">{review.name}</p>
                          <p className="text-foreground-400 text-xs">{review.date}</p>
                        </div>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i
                              className={`text-xs ${
                                i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                              }`}
                            ></i>
                          </div>
                        ))}
                      </div>
                      <p className="text-foreground-600 text-sm leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70" id="contact">
                <h2 className="font-heading text-xl text-foreground-900 mb-4">联系我们</h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  如果您有任何建议、合作意向或问题反馈，欢迎通过以下方式联系我们。
                  我们期待与您的交流！
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@budingmanhua.com' },
                    { icon: 'ri-customer-service-line', label: '客服热线', value: '400-888-6666' },
                    { icon: 'ri-wechat-line', label: '微信公众号', value: '布丁漫画' },
                    { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00-21:00' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className={`${item.icon} text-accent-600`}></i>
                      </div>
                      <div>
                        <p className="text-foreground-400 text-xs mb-0.5">{item.label}</p>
                        <p className="text-foreground-800 font-medium text-sm">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}